import React from 'react';

export interface BaseStudentBillItem {
  invoidId: string;
  billId?: string;
  studentInvoiceId: number;
  studentId: number;
  studentName: string;
  studentCode: string;
  className: string;
  classYearName?: string;
  schoolYear: string;
  cost: number;
  content: string;
  description: string;
  status: number;
  paymentDate: string | null;
  notificationCount?: number;
  invoiceStatus?: number;
  exportDate?: string | null;
  isEinvoice?: boolean;
}

export interface StudentBillGroup<T extends BaseStudentBillItem> {
  groupKey: string;
  studentId: number;
  studentCode: string;
  studentName: string;
  className: string;
  schoolYear: string;
  bills: T[];
  billCount: number;
  totalAmount: number;
  totalNotificationCount: number;
  contentSummary: string;
  latestPaymentDate: string | null;
  latestExportDate: string | null;
}

export interface GroupSelectionState {
  selectableIds: number[];
  selectedCount: number;
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
}

export const resolveStudentGroupKey = (item: Pick<BaseStudentBillItem, 'studentId' | 'studentInvoiceId'>): string => {
  if (item.studentId && item.studentId > 0) {
    return `student_${item.studentId}`;
  }
  return `invoice_${item.studentInvoiceId}`;
};

export const groupBillsByStudent = <T extends BaseStudentBillItem>(bills: T[]): StudentBillGroup<T>[] => {
  const groupMap = new Map<string, T[]>();

  for (const bill of bills) {
    const key = resolveStudentGroupKey(bill);
    const existing = groupMap.get(key);
    if (existing) {
      existing.push(bill);
    } else {
      groupMap.set(key, [bill]);
    }
  }

  const groups: StudentBillGroup<T>[] = [];

  groupMap.forEach((studentBills, groupKey) => {
    const firstBill = studentBills[0];
    const totalAmount = studentBills.reduce((sum, item) => sum + (item.cost || 0), 0);
    const totalNotificationCount = studentBills.reduce((sum, item) => sum + (item.notificationCount || 0), 0);

    const uniqueContents = Array.from(
      new Set(
        studentBills
          .map((item) => (item.content || '').trim())
          .filter(Boolean)
      )
    );

    let latestPaymentDate: string | null = null;
    let latestExportDate: string | null = null;

    for (const item of studentBills) {
      if (item.paymentDate && (!latestPaymentDate || item.paymentDate > latestPaymentDate)) {
        latestPaymentDate = item.paymentDate;
      }
      if (item.exportDate && (!latestExportDate || item.exportDate > latestExportDate)) {
        latestExportDate = item.exportDate;
      }
    }

    groups.push({
      groupKey,
      studentId: firstBill.studentId,
      studentCode: firstBill.studentCode || '',
      studentName: firstBill.studentName || '',
      className: firstBill.className || firstBill.classYearName || '',
      schoolYear: firstBill.schoolYear || '',
      bills: studentBills,
      billCount: studentBills.length,
      totalAmount,
      totalNotificationCount,
      contentSummary: uniqueContents.join('; '),
      latestPaymentDate,
      latestExportDate,
    });
  });

  return groups;
};

export const getGroupSelectionState = <T extends BaseStudentBillItem>(
  groupBills: T[],
  selectedRowKeys: React.Key[],
  isBillSelectable: (bill: T) => boolean = () => true
): GroupSelectionState => {
  const selectedSet = new Set(selectedRowKeys);
  const selectableIds = groupBills.filter(isBillSelectable).map((b) => b.studentInvoiceId);
  const selectedCount = selectableIds.filter((id) => selectedSet.has(id)).length;
  const disabled = selectableIds.length === 0;
  const checked = !disabled && selectedCount === selectableIds.length;
  const indeterminate = !disabled && selectedCount > 0 && selectedCount < selectableIds.length;

  return {
    selectableIds,
    selectedCount,
    checked,
    indeterminate,
    disabled,
  };
};

export const toggleGroupSelectionKeys = <T extends BaseStudentBillItem>(
  groupBills: T[],
  selectedRowKeys: React.Key[],
  isBillSelectable: (bill: T) => boolean = () => true
): React.Key[] => {
  const { selectableIds, checked, disabled } = getGroupSelectionState(
    groupBills,
    selectedRowKeys,
    isBillSelectable
  );
  if (disabled) {
    return selectedRowKeys;
  }

  const groupIdSet = new Set<React.Key>(selectableIds);
  if (checked) {
    return selectedRowKeys.filter((key) => !groupIdSet.has(key));
  }

  const nextSet = new Set<React.Key>(selectedRowKeys);
  for (const id of selectableIds) {
    nextSet.add(id);
  }
  return Array.from(nextSet);
};

export const toggleSingleBillKey = (
  studentInvoiceId: number,
  shouldSelect: boolean,
  selectedRowKeys: React.Key[]
): React.Key[] => {
  if (shouldSelect) {
    if (selectedRowKeys.includes(studentInvoiceId)) {
      return selectedRowKeys;
    }
    return [...selectedRowKeys, studentInvoiceId];
  }
  return selectedRowKeys.filter((key) => key !== studentInvoiceId);
};

